// SCREENS-VIEWER (stack contract §4): a lease's screen, live, filling its box. One component for
// the Screens workspace, its standalone window and the chat needs-you overlay:
//   <LiveScreen leaseId={id} role="watch" />
// - VNC (desktops; browsers with `browser_live: "vnc"`, and every take-over): noVNC on the
//   CompanyOS relay with a viewer ticket per connection (`LiveConnection`), scaled to fit;
// - screenshots (a browser lease watched on a `browser_live: "screenshot"` stack, and the fake
//   twin, which has no relay): the picture polled from `screens.frame`.
// The pane always says what is happening: waiting / starting with step text, connecting,
// reconnecting, ended, or an error with its reason.
import type { ApiError } from "@companyos/sdk";
import { Button, cn, Spinner } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { MonitorOffIcon, TriangleAlertIcon } from "lucide-react";
import { type ReactNode, useContext, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { SessionContext } from "../use-feature";
import {
  type CreateRfb,
  LiveConnection,
  LiveRefused,
  type LiveRole,
  type LiveState,
} from "./live-connection";
import { createNoVncRfb, RfbFactoryContext } from "./novnc";
import { findLease, type Lease, relaySocketUrl, screensOverviewQuery } from "./screens-queries";

export type LiveScreenProps = {
  leaseId: string;
  role: LiveRole;
  /** Defaults to the router's API and the current company. */
  api?: Api;
  co?: string;
  /** The lease is still being opened: show this step ("Starting desktop… 12 s"). */
  starting?: string | null | undefined;
  className?: string;
};

const kindName = (l: Pick<Lease, "kind">) => (l.kind === "desktop" ? "desktop" : "browser");

export function LiveScreen(props: LiveScreenProps) {
  return props.api ? <Screen {...props} api={props.api} /> : <ScreenOnRouterApi {...props} />;
}

function ScreenOnRouterApi(props: LiveScreenProps) {
  const ctx = useRouteContext({ strict: false }) as { api?: Api };
  if (!ctx.api) return null;
  return <Screen {...props} api={ctx.api} />;
}

function Screen({
  leaseId,
  role,
  api,
  co: coProp,
  starting,
  className,
}: LiveScreenProps & { api: Api }) {
  const session = useContext(SessionContext);
  const co = coProp ?? session?.company?.id ?? "";
  const overview = useQuery({ ...screensOverviewQuery(api, co), enabled: !starting && !!co });
  const lease = overview.data ? findLease(overview.data, leaseId) : null;

  let body: ReactNode;
  let phase: string;
  if (starting) {
    phase = "starting";
    body = <Busy step={starting} />;
  } else if (overview.isPending) {
    phase = "connecting";
    body = <Busy step="Loading…" />;
  } else if (!overview.data) {
    // Never loaded: say why, never "ended" (that needs the overview to prove it).
    phase = "error";
    body = (
      <Failed
        reason={problemText(overview.error, "Couldn't load the session.")}
        onRetry={() => void overview.refetch()}
      />
    );
  } else if (!lease || lease.state === "ended") {
    phase = "ended";
    body = <Ended reason="This session has ended." />;
  } else if (lease.state === "queued") {
    phase = "waiting";
    body = (
      <Busy
        step={`Waiting for a free ${kindName(lease)}${lease.position ? ` · #${lease.position}` : ""}`}
      />
    );
  } else if (lease.state === "starting") {
    phase = "starting";
    body = <Busy step={`Starting the ${kindName(lease)}…`} />;
  } else {
    phase = "live";
    body = <LiveCanvas key={lease.id} api={api} co={co} lease={lease} role={role} />;
  }
  return (
    <div
      data-slot="live-screen"
      data-lease={leaseId}
      data-role={role}
      data-stage={phase}
      className={cn("relative min-h-0 min-w-0 flex-1 overflow-hidden bg-sunken", className)}
    >
      {body}
    </div>
  );
}

function LiveCanvas({
  api,
  co,
  lease,
  role,
}: {
  api: Api;
  co: string;
  lease: Lease;
  role: LiveRole;
}) {
  const injected = useContext(RfbFactoryContext);
  const factory: CreateRfb | null = injected ?? (api.mode === "http" ? createNoVncRfb : null);
  // A watched browser on a screenshot stack is a picture stream; take-over always opens VNC.
  const frames =
    !factory || (lease.kind === "browser" && lease.live_mode === "screenshot" && role === "watch");
  if (frames) return <FrameView api={api} co={co} leaseId={lease.id} />;
  return (
    <VncView
      api={api}
      co={co}
      leaseId={lease.id}
      role={role}
      createRfb={factory}
      resizeRemote={lease.kind === "desktop"}
    />
  );
}

function ticketError(e: unknown): unknown {
  const err = e as Partial<ApiError> | null;
  const code = err?.problem?.code ?? null;
  if (code === "screens_lease_state") return new LiveRefused("ended", "This session has ended.");
  // A control ticket refused: control was handed back (4401) or taken by someone else; watch.
  if (code === "screens_controlled") return new LiveRefused("watch", "Someone else is driving.");
  if (err?.status === 403 || err?.status === 404)
    return new LiveRefused("error", "You can't watch this screen.");
  if (err?.status === 401) return new LiveRefused("error", "You're signed out. Sign in again.");
  return e;
}

function VncView({
  api,
  co,
  leaseId,
  role,
  createRfb,
  resizeRemote,
}: {
  api: Api;
  co: string;
  leaseId: string;
  role: LiveRole;
  createRfb: CreateRfb;
  resizeRemote: boolean;
}) {
  const target = useRef<HTMLElement>(null);
  const conn = useRef<LiveConnection | null>(null);
  const [state, setState] = useState<LiveState>({
    phase: "connecting",
    step: "Connecting…",
    reason: null,
    role,
    failures: 0,
  });
  const roleRef = useRef(role);
  roleRef.current = role;

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const c = new LiveConnection({
      target: el,
      role: roleRef.current,
      createRfb,
      resizeRemote,
      fetchTicket: (r) =>
        api.client.screens
          .ticket({ params: { co, id: leaseId }, body: { role: r } })
          .catch((e: unknown) => {
            throw ticketError(e);
          }),
      socketUrl: (u) => relaySocketUrl(u, api.baseUrl),
      onState: setState,
    });
    conn.current = c;
    c.start();
    return () => {
      c.stop();
      conn.current = null;
    };
  }, [api, co, leaseId, createRfb, resizeRemote]);

  useEffect(() => {
    conn.current?.setRole(role);
  }, [role]);

  // Fit the remote screen to the pane on every pane or window resize (scaled, aspect kept).
  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const refit = () => conn.current?.refit();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(refit);
    ro?.observe(el);
    window.addEventListener("resize", refit);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", refit);
    };
  }, []);

  return (
    <div
      className="absolute inset-0"
      data-slot="vnc-view"
      data-phase={state.phase}
      data-role={state.role}
    >
      <section
        ref={target}
        className="absolute inset-0"
        data-slot="vnc-target"
        aria-label="Live screen"
      />
      {state.phase === "live" ? null : state.phase === "ended" ? (
        <Ended reason={state.reason ?? "This session has ended."} />
      ) : state.phase === "error" ? (
        <Failed
          reason={state.reason ?? "Couldn't connect."}
          onRetry={() => conn.current?.retry()}
        />
      ) : (
        <Busy
          step={state.step ?? "Connecting…"}
          note={state.phase === "reconnecting" ? state.reason : null}
        />
      )}
    </div>
  );
}

function Cover({ children, slot }: { children: ReactNode; slot: string }) {
  return (
    <div
      data-slot={slot}
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-sunken/90 p-4 text-center text-sm text-muted-foreground"
    >
      {children}
    </div>
  );
}

function Busy({ step, note }: { step: string; note?: string | null }) {
  return (
    <Cover slot="live-busy">
      <Spinner className="size-5" aria-hidden="true" />
      <span className="font-medium text-foreground" data-slot="live-step" role="status">
        {step}
      </span>
      {note ? <span className="text-xs">{note}</span> : null}
    </Cover>
  );
}

function Ended({ reason }: { reason: string }) {
  return (
    <Cover slot="live-ended">
      <MonitorOffIcon className="size-5" aria-hidden="true" />
      <span className="font-medium text-foreground">Session ended</span>
      <span className="text-xs">{reason}</span>
    </Cover>
  );
}

function Failed({ reason, onRetry }: { reason: string; onRetry?: () => void }) {
  return (
    <Cover slot="live-error">
      <TriangleAlertIcon className="size-5 text-warning-text" aria-hidden="true" />
      <span className="font-medium text-foreground" role="alert">
        {reason}
      </span>
      {onRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Cover>
  );
}

/** Frame polls (one in flight at a time) that may fail in a row before the pane says so. */
const FRAME_MAX_FAILURES = 5;
/** One frame request's deadline. */
const FRAME_DEADLINE_MS = 5_000;

/**
 * The screenshot stream (contract §4.3: 1–2 fps): the screen's picture every second. A refusal
 * (the lease ended, or I may no longer watch) stops it; repeated failures show an error with
 * Try again; a picture that could not be refreshed is marked stale, never shown as current.
 */
export function FrameView({ api, co, leaseId }: { api: Api; co: string; leaseId: string }) {
  // Failed polls in a row (TanStack's own count restarts with every poll when retry is off).
  const fails = useRef(0);
  const frame = useQuery({
    queryKey: ["company", co, "screens", "frame", leaseId],
    queryFn: async ({ signal }) => {
      // A poll that hangs is a failed poll: abort it after the deadline.
      const ctl = new AbortController();
      const stop = () => ctl.abort();
      signal.addEventListener("abort", stop);
      const timer = setTimeout(stop, FRAME_DEADLINE_MS);
      try {
        const f = await api.client.screens.frame({
          params: { co, id: leaseId },
          signal: ctl.signal,
        });
        fails.current = 0;
        return f;
      } catch (e) {
        fails.current += 1;
        throw e;
      } finally {
        clearTimeout(timer);
        signal.removeEventListener("abort", stop);
      }
    },
    refetchInterval: (q) => {
      const err = q.state.error as Partial<ApiError> | null;
      if (q.state.status === "error" && refusal(err)) return false;
      if (fails.current >= FRAME_MAX_FAILURES) return false;
      return 1_000;
    },
    retry: false,
    // Only the interval polls: a refusal or a give-up is not undone by focus or reconnect.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const err = frame.error as Partial<ApiError> | null;
  const refused = frame.isError ? refusal(err) : null;
  if (refused === "ended") return <Ended reason="This session has ended." />;
  if (refused === "forbidden") return <Failed reason="You can't watch this screen." />;
  if (refused === "signed_out") return <Failed reason="You're signed out. Sign in again." />;
  const failures = fails.current;
  if (frame.isError && failures >= FRAME_MAX_FAILURES)
    return (
      <Failed
        reason={`No picture after ${failures} tries.`}
        onRetry={() => {
          fails.current = 0;
          void frame.refetch();
        }}
      />
    );
  if (!frame.data) return <Busy step={frame.isError ? "Retrying…" : "Loading the screen…"} />;
  const stale = frame.isError;
  return (
    <>
      <img
        data-slot="frame-view"
        data-stale={stale || undefined}
        className={cn(
          "absolute inset-0 h-full w-full object-contain object-top",
          stale && "opacity-50 grayscale",
        )}
        alt="The screen"
        src={`data:image/png;base64,${frame.data.image_png_base64}`}
      />
      {stale ? (
        <span
          data-slot="frame-stale"
          role="status"
          className="absolute top-2 left-1/2 -translate-x-1/2 rounded-sm bg-warning-soft px-2 py-0.5 text-xs text-warning-text"
        >
          Picture not updating · retrying
        </span>
      ) : null}
    </>
  );
}

function refusal(err: Partial<ApiError> | null): "ended" | "forbidden" | "signed_out" | null {
  if (err?.problem?.code === "screens_lease_state") return "ended";
  if (err?.status === 401) return "signed_out";
  if (err?.status === 403 || err?.status === 404) return "forbidden";
  return null;
}
